/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, FC } from 'react';
import { Text, View } from 'react-native';
import {
  VictoryLine,
  VictoryAxis,
  VictoryChart,
  VictoryScatter,
  VictoryLabel,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import Colors from '../constants/Colors';
import GetUtilitiesMetricByDate from '../requests/Sensors/GetUtilitiesMetricByDate';
import { axisDateLabel, tickValSamples, tooltipDateLabel } from './axisUtils';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import { Metric } from '../constants/Types';

type Props = {
  dates: { start: any; end: any };
  compareDates: { start: any; end: any };
  metricName: string;
  title: string;
  compareToggled: boolean;
  thingId?: string;
  unit?: string;
  thingRoom?: string;
};
const CompareLineGraphWeb: FC<Props> = ({
  dates,
  compareDates,
  metricName,
  title,
  compareToggled,
  thingId,
  unit,
}) => {
  const [graphData, setGraphData] = useState([]);
  const [compareGraphData, setCompareGraphData] = useState([]);
  const [xArray2, setXArray2] = useState([]);
  const [xArray, setXArray] = useState([]);
  const [tickVals, setTickVals] = useState([]);
  const [tickVals2, setTickVals2] = useState([]);
  const [maxVal, setMaxVal] = useState(10);
  const [dimensions, setDimensions] = useState({ width: 100, height: 100 });

  // eslint-disable-next-line no-unused-vars
  const [option, setOption] = useState(null);
  const pointsArray = [];
  const comparePointsArray = [];
  let point = {};
  let comparePoint = {};

  const Score = [];
  const Score2 = [];

  const averageMetrics: Metric[] = [
    Metric.TEMPERATURE, Metric.NOISE, Metric.PM25, Metric.TVOC, Metric.CO2, Metric.HUMIDITY,
    Metric.LIGHT,
  ];

  useEffect(() => {
    setXArray2(compareGraphData?.map((e) => e.name));
    setXArray(graphData?.map((e) => e.name));
    const tickArray = [...Array(graphData.length).keys()];
    const tickArray2 = [...Array(compareGraphData.length).keys()];
    setTickVals(tickValSamples(tickArray, 7));
    setTickVals2(tickValSamples(tickArray2, 7));
    let maxScore = 10;
    graphData.forEach((element) => {
      if (element.Score > 1) {
        maxScore = element.Score;
      }
    });
    setMaxVal(maxScore);
  }, [compareGraphData, graphData]);

  useEffect(() => {
    const getData = async () => {
      try {
        if (metricName === 'emissions') {
          GetUtilitiesMetricByDate(
            dates,
            null,
          ).then((data) => {
            let index = 0;
            if (data !== undefined) {
              data[0]?.forEach((e) => {
                Score.push(e.value);
                point = {
                  Score: Math.floor(((e.value * 179) / 1000) * 10) / 10,
                  index,
                  date: e.timestamp,
                };
                pointsArray.push(point);
                index += 1;
              });
              setGraphData(pointsArray);
            } else {
              setGraphData([]);
            }
          });
        } else if (averageMetrics.includes(Metric[metricName as keyof typeof Metric])) {
          GetMetric({
            metric: metricName,
            range: dates,
            aggregationFunction: AggregationFunction.AVG,
          })
            ?.then((data) => {
              data?.forEach((e: { value: number, timestamp: string }, index: number) => {
                Score.push(e.value);
                point = {
                  Score: Math.floor(e.value * 10) / 10,
                  index,
                  date: e.timestamp,
                };
                pointsArray.push(point);
              });
              setGraphData(pointsArray);
            });
        } else if (metricName === Metric.OCCUPANCY) {
          GetMetric({
            metric: metricName,
            range: dates,
            aggregationFunction: AggregationFunction.MAX,
          })
            ?.then((data) => {
              data?.forEach((e: { value: number, timestamp: string }, index: number) => {
                Score.push(e.value);
                point = {
                  Score: Math.floor(e.value * 10) / 10,
                  index,
                  date: e.timestamp,
                };
                pointsArray.push(point);
              });
              setGraphData(pointsArray);
            });
        } else {
          GetMetric({
            metric: metricName,
            range: dates,
            aggregationFunction: AggregationFunction.SUM,
          })
            ?.then((data) => {
              data?.forEach((e: { value: number, timestamp: string }, index: number) => {
                Score.push(e.value);
                point = {
                  Score: Math.floor(e.value * 10) / 10,
                  index,
                  date: e.timestamp,
                };
                pointsArray.push(point);
              });
              setGraphData(pointsArray);
            });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        // console.error(e, e.stack);
      }
    };
    getData();
  }, [dates.start, thingId, metricName]);
  useEffect(() => {
    const getData2 = async () => {
      try {
        if (metricName === 'emissions') {
          GetUtilitiesMetricByDate(
            compareDates,
            null,
          ).then((data) => {
            let index = 0;
            if (data !== undefined) {
              data[0]?.forEach((e) => {
                Score.push(e.value);
                point = {
                  Score: Math.floor(((e.value * 179) / 1000) * 10) / 10,
                  index,
                  date: e.timestamp,
                  compare: true,
                };
                comparePointsArray.push(point);
                index += 1;
              });
              setCompareGraphData(comparePointsArray);
            } else {
              setCompareGraphData([]);
            }
          });
        } else if (averageMetrics.includes(Metric[metricName as keyof typeof Metric])) {
          GetMetric({
            metric: metricName,
            range: compareDates,
            aggregationFunction: AggregationFunction.AVG,
          })
            ?.then((data) => {
              data?.forEach((e: { value: number, timestamp: string }, index: number) => {
                Score2.push(e.value);
                point = {
                  Score: Math.floor(e.value * 10) / 10,
                  index,
                  date: e.timestamp,
                };
                comparePointsArray.push(point);
              });
              setCompareGraphData(comparePointsArray);
            });
        } else if (metricName === Metric.OCCUPANCY) {
          GetMetric({
            metric: metricName,
            range: compareDates,
            aggregationFunction: AggregationFunction.MAX,
          })
            ?.then((data) => {
              data?.forEach((e: { value: number, timestamp: string }, index: number) => {
                Score2.push(e.value);
                point = {
                  Score: Math.floor(e.value * 10) / 10,
                  index,
                  date: e.timestamp,
                };
                comparePointsArray.push(point);
              });
              setCompareGraphData(comparePointsArray);
            });
        } else {
          GetMetric({
            metric: metricName,
            range: compareDates,
            aggregationFunction: AggregationFunction.SUM,
          })
            ?.then((data) => {
              if (data) {
                data?.forEach((e: { value: number, timestamp: string }, index: number) => {
                  Score2.push(e.value);
                  comparePoint = {
                    Score: Math.floor(e.value * 10) / 10,
                    index,
                    date: e.timestamp,
                    compare: true,
                  };
                  comparePointsArray.push(comparePoint);
                });
              }
              setCompareGraphData(comparePointsArray);
            });
        }
      } catch (error) {
        // console.error(error);
      }
    };
    getData2();
  }, [compareDates.start, thingId, metricName]);
  class CustomFlyout extends React.Component {
    render() {
      const { x, y, datum } = this.props as any;
      const containerY: number = Math.max(0, y - 70);
      const dateLabel: string = tooltipDateLabel(datum.compare ? compareGraphData
        : graphData, datum.index);
      const containerWidth: number = Math.max(100, dateLabel.length * 6);
      const containerX: number = Math.max(20, x - containerWidth + 22);
      return (
        <svg>
          <defs>
            <filter id="f3" x={-0.5} y={-1} width="200%" height="250%">
              <feOffset result="offOut" in="SourceGraphic" dx="0" dy="0" />
              <feColorMatrix
                result="matrixOut"
                in="offOut"
                type="matrix"
                values="0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0"
              />
              <feGaussianBlur result="blurOut" in="matrixOut" stdDeviation="4" />
              <feBlend in="SourceGraphic" in2="blurOut" mode="normal" result="colored" />
            </filter>
          </defs>
          <rect
            x={containerX}
            y={containerY}
            width={containerWidth}
            height={60}
            stroke="none"
            rx={7}
            fill="white"
            filter="url(#f3)"
          />
          <text
            x={containerX + 5}
            y={containerY + 15}
            fontFamily="sans-serif"
            fontSize={10}
            strokeWidth={0.3}
            fill={'#696D70'}
            stroke={'#696D70'}
          >
            {/* @ts-ignore */}
            {this.props.title}
          </text>
          <text
            x={containerX + 5}
            y={containerY + 31}
            fontFamily="sans-serif"
            fontSize={10}
            strokeWidth={0.3}
            fill={'#696D70'}
            stroke={'#696D70'}
          >
            {dateLabel}
          </text>
        </svg>
      );
    }
  }
  const chartTheme = {
    axis: {
      stroke: 'none',
      style: {
        stroke: '#696D70',
        tickLabels: {
          fill: '#696D70',
        },
        grid: { stroke: 'none' },
      },
    },
  };

  return (
    <View
      style={{ flex: 1, minHeight: 400, maxHeight: 550 }}
      onLayout={(event) => setDimensions({
        width: event.nativeEvent.layout.width,
        height: event.nativeEvent.layout.height,
      })}>
      <VictoryChart
        singleQuadrantDomainPadding={{ x: false }}
        width={dimensions.width}
        height={dimensions.height}
        theme={chartTheme}
        domainPadding={{ x: [40, 40], y: [0, 0] }}
        padding={{
          top: 75,
          left: 60,
          right: 70,
          bottom: 130,
        }}
        containerComponent={<VictoryVoronoiContainer />}
      >
        <VictoryAxis
          dependentAxis
          domain={{ y: [0, maxVal] }}
          crossAxis={false}
          offsetX={90}
          tickCount={5}
          fixLabelOverlap={true}
          style={{
            axis: { stroke: 'none' },
            tickLabels: { fontSize: 14, padding: 5 },
            grid: { stroke: Colors.light.hereworksWhite500, strokeWidth: 1, pointerEvents: 'none' },
          }}
          tickLabelComponent={<VictoryLabel
            style={{
              fill: Colors.light.hereworksBlack400,
              fontSize: 14,
              strokeWidth: 0,
              fontFamily: 'Poppins_400Regular',
              textAnchor: 'end',
            }}
          />}
          axisLabelComponent={<VictoryLabel
            style={{
              fill: Colors.light.hereworksBlack400,
              fontSize: 14,
              strokeWidth: 0,
              fontFamily: 'Poppins_400Regular',
              textAnchor: 'end',
            }}
            dy={-40}
          />}
          label={unit}
        />
        <VictoryAxis
          name="axis1"
          fixLabelOverlap={true}
          tickValues={tickVals}
          tickFormat={(x) => xArray[x]}
          tickCount={Math.min(Math.max(1, tickVals.length), 7)}
          style={{
            axis: { stroke: 'none' },
          }}
          tickLabelComponent={
            <VictoryLabel
              dy={18}
              style={{
                fontSize: 14,
                strokeWidth: 0,
                stroke: Colors.light.hereworksBlack400,
                fontFamily: 'Poppins_400Regular',
              }}
              text={({ datum }) => axisDateLabel(graphData, datum)}
            />
          }
        />
        <VictoryLine
          x="index"
          y="Score"
          data={graphData}
          interpolation="linear"
          style={{
            data: { stroke: '#003A70', strokeWidth: 2 },
          }}
        />
        <VictoryScatter
          x="index"
          y="Score"
          data={graphData}
          labels={({ datum }) => datum.Score}
          size={({ active }) => (active ? 5 : graphData.length > 10 ? 1.5 : 3)}
          style={{
            data: { fill: '#003A70' },
          }}
          labelComponent={
            <VictoryTooltip
              style={{
                fill: ({ datum }) => (datum.Score > 65 ? '#025157' : '#C07E00'),
                stroke: ({ datum }) => (datum.Score > 65 ? '#025157' : '#C07E00'),
                strokeWidth: 1.1,
                fontSize: 14,
              }}
              dy={-5}
              flyoutComponent={<CustomFlyout {...{ title }} />}
            />
          }
        />
        {compareToggled && (
          <VictoryLine
            x="index"
            y="Score"
            data={compareGraphData}
            interpolation="linear"
            style={{
              data: { stroke: Colors.light.hereworksYellow500, strokeWidth: 2 },
            }}
          />
        )}
        {compareToggled && (
          <VictoryScatter
            data={compareGraphData}
            x="index"
            y="Score"
            labels={({ datum }) => datum.Score}
            size={({ active }) => (active ? 5 : compareGraphData.length > 10 ? 1.5 : 4)}
            labelComponent={
              <VictoryTooltip
                style={{
                  fill: ({ datum }) => (datum.Score > 65 ? '#025157' : '#C07E00'),
                  stroke: ({ datum }) => (datum.Score > 65 ? '#025157' : '#C07E00'),
                  strokeWidth: 1.1,
                  fontSize: 14,
                }}
                dy={-5}
                flyoutComponent={<CustomFlyout {...{ title }} />}
              />
            }
            style={{
              data: { fill: Colors.light.hereworksYellow500 },
            }}
          />
        )}
        {compareToggled && (
          <VictoryAxis
            name="axis2"
            fixLabelOverlap={true}
            tickValues={tickVals2}
            tickCount={Math.min(Math.max(1, tickVals2.length), 7)}
            tickFormat={(x) => xArray2[x]}
            domainPadding={{ x: [0, 0], y: 20 }}
            style={{
              axis: { stroke: 'none' },
            }}
            tickLabelComponent={
              <VictoryLabel
                dy={33}
                style={{
                  fill: Colors.light.hereworksYellow500,
                  fontSize: 14,
                  strokeWidth: 0,
                  fontFamily: 'Poppins_400Regular',
                }}
                text={({ datum }) => axisDateLabel(compareGraphData, datum)}
              />
            }
          />
        )}
      </VictoryChart>
      {!graphData.length && (!compareToggled || !compareGraphData.length) && (
        <View
          style={{
            position: 'absolute',
            alignSelf: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Text
            style={{
              color: Colors.light.hereworksBlack400,
              fontFamily: 'Roboto_400Regular',
              fontSize: 16,
            }}
          >
            No data available
          </Text>
        </View>
      )}
    </View>
  );
};

export default CompareLineGraphWeb;
